/**
 * 自定义 request 网络请求工具, 基于 axios
 * @author LiQingSong
 */
import axios, { AxiosPromise, AxiosRequestConfig, AxiosResponse } from 'axios';
import { notification, message } from 'antd';
import settings from '@/config/settings';
import { getToken, clearSession, loginUrl } from '@/utils/localToken';

export interface ResponseData<T = unknown> {
  code: number;
  data?: T;
  msg?: string;
  result?: T;
  resolveOnError?: boolean;
}

const serverCodeMessage: { [key: number]: string } = {
  200: '服务器成功返回请求的数据',
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  500: '服务器发生错误，请检查服务器(Internal Server Error)',
  502: '网关错误(Bad Gateway)',
  503: '服务不可用，服务器暂时过载或维护(Service Unavailable)',
  504: '网关超时(Gateway Timeout)',
};

/**
 * 错误处理函数
 */
const errorHandler = (error: any) => {
  const { response } = error;
  // 文件流类型错误处理
  if (
    response &&
    response.status === 200 &&
    response.request?.responseType === 'blob'
  ) {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const json = JSON.parse(reader.result as string);
        notification.error({
          message: '错误提示',
          description: json.msg || '文件下载失败',
        });
      } catch {
        notification.error({
          message: '文件下载失败',
          description: '返回的内容无法识别',
        });
      }
    };
    reader.readAsText(response.data);
    return Promise.reject(error);
  }

  // 有响应时的统一处理
  if (response && response.status) {
    const { status, request, data } = response;
    // const errorText = serverCodeMessage[status] || response.statusText;

    if (status === 401) {
      // An old request must not clear a newer account's session.
      const sentToken = response.config?.headers?.[settings.ajaxHeadersTokenKey];
      if (sentToken && sentToken === getToken()) {
        clearSession();
        message.error('请重新登录');
        if (window.location.pathname !== '/user/login') window.location.replace(loginUrl());
      }
      return Promise.reject(error);
    } else {
      message.error(data?.desc || data?.errorMsg || data?.errorBody?.zhDesc || '请求失败');
    }
    if (response.config.resolveOnError) {
      return Promise.resolve(response);
    } else {
      return Promise.reject({ response });
    }
  }

  // 无响应（如断网）
  if (!response) {
    notification.error({
      message: '网络异常',
      description: '您的网络发生异常，无法连接服务器',
    });
  }

  return Promise.reject(error);
};

const request = axios.create({
  baseURL: import.meta.env.VITE_APP_APIHOST || '',
  withCredentials: false,
  timeout: 0,
});

// 请求拦截器
request.interceptors.request.use(
  (config: AxiosRequestConfig & { cType?: boolean }) => {
    if (config.cType) {
      config.headers = config.headers || {};
      config.headers['Content-Type'] = 'application/x-www-form-urlencoded;charset=UTF-8';
    }

    const token = getToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers[settings.ajaxHeadersTokenKey] = token;
    }

    return config;
  }
);

// 响应拦截器
request.interceptors.response.use(
  (response: AxiosResponse<ResponseData>) => {
    const contentType = response.headers['content-type'] || '';
    const isBlob =
      response.request?.responseType === 'blob' ||
      contentType.includes('application/octet-stream') ||
      contentType.includes('application/pdf') ||
      contentType.includes('application/vnd');

    if (isBlob) {
      return response; // 文件流直接返回
    }

    const res: any = response.data;
    const config: any = response.config
    if (res.code !== 0) {
      if (config.resolveOnError) {
        message.error(res?.errorMsg || res?.errorBody?.zhDesc || '操作失败');
        return Promise.resolve(response);
      } else {
        return Promise.reject({ response });
      }
    }

    return response;
  }
);

/**
 * ajax 请求方法
 */
export default function ajax<T = any, R = AxiosResponse<T>>(
  config: AxiosRequestConfig & { cType?: boolean, resolveOnError?: boolean },
): AxiosPromise<R> {
  return request(config)
    .then((response: AxiosResponse) => response.data)
    .catch((error) => errorHandler(error));
}
